import React, { useContext, useState } from "react";
import { useValidators } from "formvalidation/hooks";
import ValidatorContext from "formvalidation/FormContext";
import * as Sentry from "@sentry/browser";
import { SidePanelSectionTemplate } from "./SidePanelSectionTemplate";
import { AxiosPromise } from "node_modules/axios";
import { EditNewSectionField } from "../types";
import { SidePanelContent, SidePanelFooter, SidePanelHeader } from "components/SidePanel/SidePanel";
import { ButtonPrimary } from "components/Atoms/Button";
import { Message } from "semantic-ui-react";
import { AfterCreateAction } from "components/SidePanel/types";
import { SidePanelContext } from "components/SidePanel/SidePanelContext";

interface SidePanelNewSectionTemplateProps {
  title: string;
  id: string;
  trigger: JSX.Element;

  submit: () => AxiosPromise<server.dto.ApiResponseWrapper<number>>;
  top?: JSX.Element;
  fields: Array<EditNewSectionField>;

  saveDisabled?: boolean;
  saveButtonLabel?: string;
  onSave: (id: number) => void;
  afterCreate: AfterCreateAction;
}

export const SidePanelNewSectionTemplate = (props: SidePanelNewSectionTemplateProps) => {
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { closePanel, back, hasBack } = useContext(SidePanelContext);

  const validators = useValidators();

  const onSubmit = async () => {
    if (isLoading) return;

    setIsLoading(true);
    const validatorResult = await validators.validateAll();

    if (validatorResult.hasErrors) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await props.submit();
      setIsLoading(false);

      setServerErrors(response.data.errors.map(e => e.message));

      if (response.data.hasErrors) {
        return;
      }

      props.onSave(response.data.data);

      if (props.afterCreate === "closeSidebar") {
        closePanel();
      } else if (props.afterCreate === "goBack") {
        if (hasBack) {
          back();
        } else {
          throw new Error("Cannot go back because there is no previous panel in the stack");
        }
      }
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      setIsLoading(false);
      setServerErrors([RESX.GeneralLabels.SomethingUnexpectedHappened]);
    }
  }

  const mappedFields = props.fields.map(field => ({
    ...field,
    isEmpty: false,
    render: field.renderEditNew,
    inPageEdit: undefined,
  }));

  return <ValidatorContext settings={validators.validatorSettings}>
    <SidePanelHeader title={props.title} />
    <SidePanelContent>
      <div className="flex-column gap-larger">
        {props.top}
        <SidePanelSectionTemplate
          fields={mappedFields}
          compact
          isInPageEditable={false}
          displayEmptyValues
        />
      </div>
    </SidePanelContent>
    <SidePanelFooter>
      <div>
        {serverErrors.length > 0 && <Message color="red">
          {serverErrors.map((error) => <div key={error}>{error}</div>)}
        </Message>}
      </div>
      <ButtonPrimary
        onClick={onSubmit}
        loading={isLoading}
        label={props.saveButtonLabel ?? RESX.Buttons.btnSave}
        disabled={props.saveDisabled}
      />
    </SidePanelFooter>
  </ValidatorContext>
}